// User Preferences Management

export interface UserPreferences {
    favorites: string[]; // Commodity keys
    watchlist: string[]; // Custom category of items to watch
    priceAlerts: PriceAlert[];
    theme: 'light' | 'dark' | 'auto'; // Future: dark mode
    defaultCategory: string;
    notifications: {
        enabled: boolean;
        priceChanges: boolean;
        dailySummary: boolean;
    };
}

export interface PriceAlert {
    id: string;
    commodityKey: string;
    condition: 'above' | 'below' | 'change';
    value: number; // Target price or % change
    active: boolean;
    lastTriggered?: number; // Timestamp
}

const STORAGE_KEY = 'mp-preferences';

// Default preferences
const DEFAULT_PREFERENCES: UserPreferences = {
    favorites: [],
    watchlist: [],
    priceAlerts: [],
    theme: 'light',
    defaultCategory: 'essentials',
    notifications: {
        enabled: false,
        priceChanges: false,
        dailySummary: false,
    },
};

// Load preferences from localStorage
export function loadPreferences(): UserPreferences {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return { ...DEFAULT_PREFERENCES };

        const parsed = JSON.parse(stored);

        // Merge with defaults to handle new properties
        return {
            ...DEFAULT_PREFERENCES,
            ...parsed,
            notifications: {
                ...DEFAULT_PREFERENCES.notifications,
                ...parsed.notifications,
            },
        };
    } catch (error) {
        console.error('Failed to load preferences:', error);
        return { ...DEFAULT_PREFERENCES };
    }
}

// Save preferences to localStorage
export function savePreferences(prefs: UserPreferences): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch (error) {
        console.error('Failed to save preferences:', error);
    }
}

// Favorites
export function addFavorite(preferences: UserPreferences, commodityKey: string): UserPreferences {
    if (preferences.favorites.includes(commodityKey)) {
        return preferences;
    }

    const updated = {
        ...preferences,
        favorites: [...preferences.favorites, commodityKey],
    };

    savePreferences(updated);
    return updated;
}

export function removeFavorite(preferences: UserPreferences, commodityKey: string): UserPreferences {
    const updated = {
        ...preferences,
        favorites: preferences.favorites.filter(k => k !== commodityKey),
    };

    savePreferences(updated);
    return updated;
}

export function isFavorite(preferences: UserPreferences, commodityKey: string): boolean {
    return preferences.favorites.includes(commodityKey);
}

// Watchlist
export function addToWatchlist(preferences: UserPreferences, commodityKey: string): UserPreferences {
    if (preferences.watchlist.includes(commodityKey)) {
        return preferences;
    }

    const updated = {
        ...preferences,
        watchlist: [...preferences.watchlist, commodityKey],
    };

    savePreferences(updated);
    return updated;
}

export function removeFromWatchlist(preferences: UserPreferences, commodityKey: string): UserPreferences {
    const updated = {
        ...preferences,
        watchlist: preferences.watchlist.filter(k => k !== commodityKey),
    };

    savePreferences(updated);
    return updated;
}

// Price Alerts
export function addPriceAlert(preferences: UserPreferences, alert: Omit<PriceAlert, 'id'>): UserPreferences {
    const newAlert: PriceAlert = {
        ...alert,
        id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };

    const updated = {
        ...preferences,
        priceAlerts: [...preferences.priceAlerts, newAlert],
    };

    savePreferences(updated);
    return updated;
}

export function removePriceAlert(preferences: UserPreferences, alertId: string): UserPreferences {
    const updated = {
        ...preferences,
        priceAlerts: preferences.priceAlerts.filter(a => a.id !== alertId),
    };

    savePreferences(updated);
    return updated;
}

export function togglePriceAlert(preferences: UserPreferences, alertId: string): UserPreferences {
    const updated = {
        ...preferences,
        priceAlerts: preferences.priceAlerts.map(alert =>
            alert.id === alertId ? { ...alert, active: !alert.active } : alert
        ),
    };

    savePreferences(updated);
    return updated;
}

// Check alerts and return triggered ones
export function checkPriceAlerts(
    preferences: UserPreferences,
    commodityKey: string,
    currentPrice: number | null,
    percentChange: number | null
): PriceAlert[] {
    if (currentPrice === null) return [];

    const now = Date.now();
    const cooldownMs = 24 * 60 * 60 * 1000; // 24 hours

    return preferences.priceAlerts.filter(alert => {
        if (!alert.active) return false;
        if (alert.commodityKey !== commodityKey) return false;
        if (alert.lastTriggered && (now - alert.lastTriggered < cooldownMs)) return false;

        switch (alert.condition) {
            case 'above':
                return currentPrice > alert.value;
            case 'below':
                return currentPrice < alert.value;
            case 'change':
                return percentChange !== null && Math.abs(percentChange) >= alert.value;
            default:
                return false;
        }
    });
}

export function markAlertTriggered(preferences: UserPreferences, alertId: string): UserPreferences {
    const updated = {
        ...preferences,
        priceAlerts: preferences.priceAlerts.map(alert =>
            alert.id === alertId ? { ...alert, lastTriggered: Date.now() } : alert
        ),
    };

    savePreferences(updated);
    return updated;
}
